import React, { FC } from "react";
import { Roles } from "../../../types/roles";
import {
  ConvertedKrookie,
  DocumentOrigin,
  KrookieStatus,
} from "../../../interfaces/krookie";
import styles from "./krookie-action-panel.module.scss";
import Button from "../../../components/ui-kit/button/button";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { useTranslation } from "react-i18next";
import { KrookieFormationTypeId } from "../../../services/reducers/master-plan-modification";

interface KrookieActionPanelProps {
  origin: DocumentOrigin;
  status: KrookieStatus;
  onEdit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onSubmitForApproval: () => void;
  onReturn: () => void;
  onPrint: () => void;
  onSave: (status?: KrookieStatus) => void;
  convertedKrookieData?: ConvertedKrookie;
  krookieFormationTypeId: KrookieFormationTypeId;
  onSaveAndSeeNext: (status?: KrookieStatus) => void;
  hasMultipleKrookies: boolean;
  isLastUnsavedKrookie: boolean;
  onSubmitForApprovalAndSeeNext: () => void;
  onApproveAndSeeNext: () => void;
  isRequestInProgress: boolean;
}

export const KrookieActionPanel: FC<KrookieActionPanelProps> = ({
  origin,
  status,
  onEdit,
  onApprove,
  onReject,
  onSubmitForApproval,
  onReturn,
  onPrint,
  onSave,
  convertedKrookieData,
  onSaveAndSeeNext,
  onSubmitForApprovalAndSeeNext,
  hasMultipleKrookies,
  isLastUnsavedKrookie,
  onApproveAndSeeNext,
  isRequestInProgress,
}) => {
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.user);
  const isCreator = user?.roles.includes(Roles.KrookieCreator);
  const isApprover = user?.roles.includes(Roles.KrookieApprover);
  const isJournal = user?.roles.includes(Roles.KrookieJournal);

  const shouldUseColumnLayout = hasMultipleKrookies && !isLastUnsavedKrookie;

  const getSaveButtonText = () => {
    if (hasMultipleKrookies && !isLastUnsavedKrookie) {
      return t("document-krookie.saveKrookieAndSeeNext");
    } else if (
      hasMultipleKrookies &&
      isLastUnsavedKrookie &&
      status === KrookieStatus.Rejected
    ) {
      return t("document-krookie.saveAndSubmitAllForApproval");
    } else if (!hasMultipleKrookies && status === KrookieStatus.Rejected) {
      return t("document-krookie.saveAndSubmit");
    }
    return t("document-krookie.saveKrookie");
  };

  const getSaveHandler = () => {
    const statusId =
      status === KrookieStatus.Rejected
        ? KrookieStatus.NeedApproval
        : undefined;

    if (hasMultipleKrookies && !isLastUnsavedKrookie) {
      return () => onSaveAndSeeNext(statusId);
    } else if (isLastUnsavedKrookie) {
      return () => onSave(statusId);
    }
    return () => onSave();
  };

  const getSubmitForApprovalButtonText = () => {
    if (hasMultipleKrookies && !isLastUnsavedKrookie) {
      return t("document-krookie.submitForApprovalAndSeeNext");
    }
    return t("document-krookie.submitForApproval");
  };

  const getSubmitForApprovalHandler = () => {
    if (hasMultipleKrookies && !isLastUnsavedKrookie) {
      return onSubmitForApprovalAndSeeNext;
    }
    return onSubmitForApproval;
  };

  const getApproveButtonText = () => {
    if (hasMultipleKrookies && !isLastUnsavedKrookie) {
      return t("document-krookie.approveKrookieAndSeeNext");
    }
    return t("document-krookie.approveKrookie");
  };

  const getApproveHandler = () => {
    if (hasMultipleKrookies && !isLastUnsavedKrookie) {
      return onApproveAndSeeNext;
    }
    return onApprove;
  };

  if (origin === DocumentOrigin.Journal) {
    if (isCreator && isApprover) {
      switch (status) {
        case KrookieStatus.Project:
          return (
            <div className={styles.buttonSectionJP}>
              <Button
                onClick={getSubmitForApprovalHandler()}
                className={styles.btnApproval}
                type="outline"
                disabled={isRequestInProgress}
              >
                {getSubmitForApprovalButtonText()}
              </Button>
              <div className={styles.bottomBtnSection}>
                <Button
                  onClick={onEdit}
                  className={styles.editBtn}
                  type="primary"
                >
                  {t("document-krookie.editKrookie")}
                </Button>
                <Button
                  onClick={onReturn}
                  className={styles.backBtn}
                  type="outline"
                >
                  {t("document-krookie.returnToJournal")}
                </Button>
              </div>
            </div>
          );
        case KrookieStatus.NeedApproval:
          return (
            <div
              className={
                shouldUseColumnLayout
                  ? styles.buttonSectionColumn
                  : styles.buttonSection
              }
            >
              <Button
                onClick={getApproveHandler()}
                className={styles.btn}
                type="primary"
                disabled={isRequestInProgress}
              >
                {getApproveButtonText()}
              </Button>
              <Button
                onClick={onReject}
                className={styles.btn}
                type="outline"
                disabled={isRequestInProgress}
              >
                {t("document-krookie.rejectKrookie")}
              </Button>
            </div>
          );
        case KrookieStatus.Rejected:
          return (
            <div className={styles.buttonSection}>
              <Button
                onClick={onEdit}
                className={styles.editBtn}
                type="primary"
              >
                {t("document-krookie.editKrookie")}
              </Button>
              <Button
                onClick={onReturn}
                className={styles.backBtn}
                type="outline"
              >
                {t("document-krookie.returnToJournal")}
              </Button>
            </div>
          );
        case KrookieStatus.Approved:
          return (
            <div className={styles.buttonSection}>
              <Button
                onClick={onPrint}
                className={styles.editBtn}
                type="primary"
              >
                {t("document-krookie.printKrookie")}
              </Button>
              <Button
                onClick={onReturn}
                className={styles.backBtn}
                type="outline"
              >
                {t("document-krookie.returnToJournal")}
              </Button>
            </div>
          );
        default:
          return null;
      }
    }

    if (isCreator) {
      if (status === KrookieStatus.Project) {
        return (
          <div className={styles.buttonSectionJP}>
            <Button
              onClick={getSubmitForApprovalHandler()}
              className={styles.btnApproval}
              type="outline"
              disabled={isRequestInProgress}
            >
              {getSubmitForApprovalButtonText()}
            </Button>
            <div className={styles.bottomBtnSection}>
              <Button
                onClick={onEdit}
                className={styles.editBtn}
                type="primary"
              >
                {t("document-krookie.editKrookie")}
              </Button>
              <Button
                onClick={onReturn}
                className={styles.backBtn}
                type="outline"
              >
                {t("document-krookie.returnToJournal")}
              </Button>
            </div>
          </div>
        );
      } else if (
        status === KrookieStatus.NeedApproval ||
        status === KrookieStatus.Approved
      ) {
        return (
          <div className={styles.buttonSection}>
            <Button onClick={onPrint} className={styles.editBtn} type="primary">
              {t("document-krookie.printKrookie")}
            </Button>
            <Button
              onClick={onReturn}
              className={styles.backBtn}
              type="outline"
            >
              {t("document-krookie.returnToJournal")}
            </Button>
          </div>
        );
      } else if (status === KrookieStatus.Rejected) {
        return (
          <div className={styles.buttonSection}>
            <Button onClick={onEdit} className={styles.editBtn} type="primary">
              {t("document-krookie.editKrookie")}
            </Button>
            <Button
              onClick={onReturn}
              className={styles.backBtn}
              type="outline"
            >
              {t("document-krookie.returnToJournal")}
            </Button>
          </div>
        );
      }
    }

    if (isApprover) {
      if (status === KrookieStatus.NeedApproval) {
        return (
          <div className={styles.buttonSection}>
            <Button
              onClick={getApproveHandler()}
              className={styles.btn}
              type="primary"
              disabled={isRequestInProgress}
            >
              {getApproveButtonText()}
            </Button>
            <Button
              onClick={onReject}
              className={styles.btn}
              type="outline"
              disabled={isRequestInProgress}
            >
              {t("document-krookie.rejectKrookie")}
            </Button>
          </div>
        );
      }
      return (
        <div className={styles.buttonSection}>
          <Button onClick={onPrint} className={styles.editBtn} type="primary">
            {t("document-krookie.printKrookie")}
          </Button>
          <Button onClick={onReturn} className={styles.backBtn} type="outline">
            {t("document-krookie.returnToJournal")}
          </Button>
        </div>
      );
    }

    if (isJournal) {
      return (
        <div className={styles.buttonSection}>
          <Button onClick={onPrint} className={styles.editBtn} type="primary">
            {t("document-krookie.printKrookie")}
          </Button>
          <Button onClick={onReturn} className={styles.backBtn} type="outline">
            {t("document-krookie.returnToJournal")}
          </Button>
        </div>
      );
    }
  } else if (origin === DocumentOrigin.Edit) {
    if (convertedKrookieData) {
      if (status === KrookieStatus.Project) {
        return (
          <div
            className={
              shouldUseColumnLayout
                ? styles.buttonSectionColumn
                : styles.buttonSection
            }
          >
            <Button
              onClick={getSaveHandler()}
              className={styles.btn}
              type="primary"
              disabled={isRequestInProgress}
            >
              {getSaveButtonText()}
            </Button>
            <Button onClick={onReturn} className={styles.btn} type="outline">
              {t("document-krookie.backToEdit")}
            </Button>
          </div>
        );
      } else if (status === KrookieStatus.Rejected) {
        return (
          <div className={styles.buttonSectionColumn}>
            <Button
              onClick={getSaveHandler()}
              className={styles.btn}
              type="primary"
              disabled={isRequestInProgress}
            >
              {getSaveButtonText()}
            </Button>
            <Button onClick={onReturn} className={styles.btn} type="outline">
              {t("document-krookie.backToEdit")}
            </Button>
          </div>
        );
      }
    }
    return (
      <div
        className={
          shouldUseColumnLayout
            ? styles.buttonSectionColumn
            : styles.buttonSection
        }
      >
        <Button
          onClick={getSaveHandler()}
          className={styles.btn}
          type="primary"
          disabled={isRequestInProgress}
        >
          {getSaveButtonText()}
        </Button>
        <Button onClick={onReturn} className={styles.btn} type="outline">
          {t("document-krookie.backToEdit")}
        </Button>
      </div>
    );
  }

  return null;
};

export default KrookieActionPanel;
